import axios from 'axios';
import { MOCK } from './mockData';
import { authHeader, handleUnauthorized } from './auth';

const IS_MOCK = process.env.REACT_APP_MOCK_DATA === 'true';

// Everything now goes to the Node chat-server behind /api. The old /webhook
// prefix pointed at n8n, which this stack does not run.
const CHAT_BASE = process.env.NODE_ENV === 'production'
  ? '/api'
  : 'http://localhost:3001';

const BASE = process.env.REACT_APP_API_BASE || CHAT_BASE;

const api = axios.create({ baseURL: BASE, timeout: 30000 });

// Calls here are split between the shared instance and bare axios, and an
// instance does not inherit the global interceptors, so both are wired up.
// Every request to this API carries the session token; a 401 anywhere drops
// the session so the app falls back to the login screen instead of showing
// blank panels.
[axios, api].forEach((client) => {
  client.interceptors.request.use((config) => {
    const url = config.url || '';
    const sameApi = url.startsWith(CHAT_BASE) || !/^https?:\/\//i.test(url);
    if (sameApi) config.headers = { ...(config.headers || {}), ...authHeader() };
    return config;
  });
  client.interceptors.response.use(
    (r) => r,
    (err) => {
      // A 401 from the auth routes means "wrong password", not "session
      // expired". Signing the user out there would throw them back to the
      // login screen for a simple typo.
      const url = (err.config && err.config.url) || '';
      const isAuthRoute = /\/(login|change-password)$/.test(url);
      if (!isAuthRoute && err.response && err.response.status === 401) {
        handleUnauthorized();
      }
      return Promise.reject(err);
    },
  );
});

// ── Fetch latest output for a module ──────────────────────
export async function getModuleOutput(moduleId) {
  if (IS_MOCK) return MOCK[moduleId] || null;
  const { data } = await axios.get(`${CHAT_BASE}/module-output/${moduleId}`, { timeout: 15000 });
  return data;
}


// ── Motivational quotations (Module 02) ───────────────────
export async function getQuotes({ count = 6, seen = [], tag = null } = {}) {
  const params = new URLSearchParams({ count: String(count) });
  if (seen.length) params.set('seen', seen.join(','));
  if (tag) params.set('tag', tag);
  const { data } = await axios.get(`${CHAT_BASE}/quotes?${params}`, { timeout: 15000 });
  return data;
}

// ── Full wardrobe with photos (Module 06) ─────────────────
export async function getWardrobe() {
  const { data } = await axios.get(`${CHAT_BASE}/wardrobe`, { timeout: 15000 });
  return data;
}

// ── Manually re-run a module ───────────────────────────────
export async function rerunModule(moduleId) {
  if (IS_MOCK) {
    await new Promise((r) => setTimeout(r, 1500));
    return { success: true };
  }
  const { data } = await api.post('/module-rerun', { moduleId });
  return data;
}

// ── Approve and send an athlete SMS ───────────────────────
// The server resolves the destination number from the roster by athleteId and
// refuses to send without confirm:true — so this is the only call path that
// can reach a real phone, and it always carries an explicit approval.
export async function sendAthleteSMS({ athleteId, variant, message }) {
  if (IS_MOCK) {
    await new Promise((r) => setTimeout(r, 1000));
    return { success: true, dryRun: true, sid: 'MOCK_SID_' + Date.now() };
  }
  const { data } = await api.post('/send-athlete-text', {
    athleteId,
    variant,
    message,
    confirm: true,
  });
  return data;
}

// ── Register push notification subscription ───────────────
export async function registerPushSubscription(subscription) {
  if (IS_MOCK) return { success: true };
  const { data } = await api.post('/push-subscribe', subscription);
  return data;
}

// ── Live RentCast deal search (Module 07) ─────────────────
export async function searchDeals(query) {
  const { data } = await axios.post(
    `${CHAT_BASE}/deals/search`,
    { query },
    { timeout: 90000 }
  );
  return data;
}

export { CHAT_BASE, IS_MOCK };
